/**
 * The `voice` scene: an audio-only room with 8 mic seats.
 *
 * Roles: host (auto-seated) and mic listener (joined as audience, not publishing until approved).
 * Raising a hand broadcasts a `hand` intent; the host approves it, which broadcasts a `mic-approved`
 * intent that the listener turns into `setRole('host') + publishAudio()`. Seats, hand order and
 * speaking levels are owned by the pure voice machine so they are unit-tested without a browser.
 */
import { useCallback, useEffect, useReducer } from 'react';
import { useI18n } from '../i18n';
import { Banner, ChatPanel, RemoteAudio, useSessionState } from '../ui';
import {
  describeSeat,
  initialVoiceRoom,
  reduceVoice,
  voiceOccupancy,
  voiceReducer,
  VOICE_SEATS,
} from '../scenarios/machines';
import { RoomHeader, useNoticeToast } from './common';
import type { RoomContext } from './types';

export function VoiceRoom({ context }: { context: RoomContext }) {
  const { session, client, notify, onLeave } = context;
  const { t } = useI18n();
  const state = useSessionState(
    useCallback((listener) => session.subscribe(listener), [session]),
    useCallback(() => session.getState(), [session]),
  );
  useNoticeToast(session, notify);
  const [voice, dispatch] = useReducer(voiceReducer, undefined, initialVoiceRoom);

  const local = state.participants.find((entry) => entry.isLocal) ?? null;
  const isHost = (local?.role ?? context.role) === 'host';
  const localUid = state.localUid;
  const seatedLocal = local?.seated === true;
  const raisedHands = state.participants.filter(
    (entry) => !entry.isLocal && entry.handRaised && entry.role !== 'host',
  );

  // 依赖用标量（local 是对象，每次 store 更新都换引用）——同时保持「joined 且有 local 名字」语义。
  const localName = local?.displayName ?? null;
  useEffect(() => {
    if (state.phase !== 'joined' || localUid === null || localName === null) return;
    dispatch({ type: 'join', localUid, localName, role: isHost ? 'host' : 'audience' });
  }, [state.phase, localUid, localName, isHost]);

  useEffect(() => {
    for (const participant of state.participants) {
      dispatch({
        type: 'participant',
        uid: participant.uid,
        name: participant.displayName,
        role: participant.role === 'host' ? 'host' : 'audience',
      });
      dispatch({ type: 'volume', uid: participant.uid, level: participant.speaking ? 0.2 : 0 });
    }
  }, [state.participants]);

  const approve = (uid: number, name: string): void => {
    const result = reduceVoice(voice, { type: 'approve', uid });
    if (!result.allowed) {
      notify(t(result.reason ?? 'toast.seatsFull'));
      return;
    }
    dispatch({ type: 'approve', uid });
    session.setRole(uid, 'host');
    session.setSeated(uid, true);
    void session.approveMic(uid).catch(() => undefined);
    notify(t('voice.approveToast', { name }));
  };

  const raiseHand = (): void => {
    if (localUid === null) return;
    dispatch({ type: 'raise-hand', uid: localUid });
    void session.setHandRaised(true).catch(() => undefined);
    notify(t('voice.raiseToast'));
  };

  const lowerHand = (): void => {
    if (localUid === null) return;
    dispatch({ type: 'lower-hand', uid: localUid });
    void session.setHandRaised(false).catch(() => undefined);
  };

  const toggleMic = (): void => {
    if (!seatedLocal && !isHost) {
      notify(t('voice.listenerNote'));
      return;
    }
    void session.toggleMic().catch((error) => notify(String(error)));
  };

  const errorText = state.errorKey ? t(state.errorKey) : state.error;
  const seated = local?.seated === true || isHost;

  return (
    <main className="nm-voice" data-testid="scene-voice" data-scene="voice">
      <RoomHeader
        scene="voice"
        channel={state.channel ?? ''}
        onLeave={onLeave}
        meta={
          <>
            <span
              className={`nm-badge ${isHost ? 'is-host' : 'is-live'}`}
              data-testid={isHost ? 'voice-host-badge' : 'voice-listener-badge'}
            >
              {isHost ? t('voice.host') : t('voice.listener')}
            </span>
            <span className="nm-badge" data-testid="voice-occupancy">
              {t('voice.seats', { occupied: voiceOccupancy(voice), total: VOICE_SEATS })}
            </span>
            <span className="nm-badge is-muted">{t('voice.audioOnly')}</span>
          </>
        }
        actions={
          <button
            className="nm-btn"
            onClick={() => {
              void session.renew().then(() => notify(t('room.renewed')));
            }}
            data-testid="voice-renew"
          >
            {t('voice.renew')}
          </button>
        }
      />

      {errorText ? (
        <Banner tone="error">
          <span data-testid="room-error">{errorText}</span>
        </Banner>
      ) : null}
      <Banner tone={isHost ? 'info' : 'warn'}>
        <span data-testid="voice-note">
          {isHost ? t('voice.hostNote') : t('voice.listenerNote')}
        </span>
      </Banner>

      <div className="nm-voice-grid">
        <section className="nm-voice-main">
          <div
            className="nm-seat-grid"
            data-testid="voice-seats"
            aria-label={t('voice.seats', { occupied: voiceOccupancy(voice), total: VOICE_SEATS })}
          >
            {Array.from({ length: VOICE_SEATS }, (_, index) => {
              const seat = describeSeat(voice, index);
              const participant =
                state.participants.find((entry) => entry.uid === seat.uid) ?? null;
              const speaking = participant?.speaking === true && seat.uid !== null;
              return (
                <div
                  key={index}
                  className={`nm-seat${seat.uid === null ? ' is-empty' : ''}${speaking ? ' is-speaking' : ''}${seat.uid === localUid ? ' is-local' : ''}`}
                  data-testid={`voice-seat-${index}`}
                  data-uid={seat.uid ?? ''}
                >
                  <div className="nm-seat-orb" aria-hidden="true">
                    {seat.uid === null ? '＋' : initials(seat.name ?? '')}
                  </div>
                  <div className="nm-seat-label" data-testid={`voice-seat-name-${index}`}>
                    {seat.uid === null
                      ? t('voice.emptySeat')
                      : `${seat.name}${seat.uid === localUid ? ` (${t('common.you')})` : ''}`}
                  </div>
                  <div className="nm-seat-meta">
                    {seat.uid !== null ? (
                      <>
                        <span
                          className={`nm-dot${participant?.audioMuted === false ? ' is-on' : ''}`}
                          aria-hidden="true"
                        />
                        <span data-testid={`voice-seat-mic-${index}`}>
                          {participant?.audioMuted === false ? t('voice.micOn') : t('voice.micOff')}
                        </span>
                        {speaking ? (
                          <span data-testid={`voice-seat-speaking-${index}`}>
                            {t('voice.speaking')}
                          </span>
                        ) : null}
                      </>
                    ) : (
                      t('voice.seatLabel', { index: index + 1 })
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="nm-voice-controls" role="toolbar" aria-label={t('room.controls')}>
            <button
              className="nm-btn"
              aria-pressed={state.micEnabled}
              onClick={toggleMic}
              disabled={!seated}
              data-testid="voice-mic"
            >
              {state.micEnabled ? '🎙' : '🔇'}{' '}
              <span>{state.micEnabled ? t('voice.mute') : t('voice.unmute')}</span>
            </button>
            {!isHost ? (
              <button
                className="nm-btn"
                aria-pressed={local?.handRaised ?? false}
                onClick={() => (local?.handRaised ? lowerHand() : raiseHand())}
                data-testid="voice-raise-hand"
              >
                ✋ <span>{local?.handRaised ? t('voice.lowerHand') : t('voice.raiseHand')}</span>
              </button>
            ) : null}
            {seatedLocal && !isHost ? (
              <button
                className="nm-btn"
                onClick={() => {
                  dispatch({ type: 'leave-seat', uid: localUid ?? 0 });
                  void session.setSeated(localUid ?? 0, false);
                }}
                data-testid="voice-leave-seat"
              >
                {t('voice.seatLabel', { index: 1 })}
              </button>
            ) : null}
          </div>

          {isHost ? (
            <div className="nm-card nm-voice-hands" data-testid="voice-hands">
              <h2>{t('voice.hands')}</h2>
              {raisedHands.length === 0 ? <p className="nm-sub">{t('voice.noHands')}</p> : null}
              {raisedHands.map((participant) => (
                <div
                  className="nm-person"
                  key={participant.uid}
                  data-testid={`voice-hand-${participant.uid}`}
                >
                  <div className="nm-person-main">
                    <div className="nm-person-name">{participant.displayName}</div>
                    <div className="nm-person-meta">{t('voice.listener')}</div>
                  </div>
                  <button
                    className="nm-btn is-active"
                    onClick={() => approve(participant.uid, participant.displayName)}
                    data-testid={`voice-approve-${participant.uid}`}
                  >
                    {t('voice.approve')}
                  </button>
                </div>
              ))}
            </div>
          ) : null}
          {state.participants
            .filter((participant) => !participant.isLocal)
            .map((participant) => (
              <RemoteAudio
                key={`voice-audio-${participant.uid}`}
                stream={client.getRemoteStream(participant.uid, 'audio')}
              />
            ))}
        </section>

        <aside className="nm-voice-side">
          <ChatPanel
            state={state}
            testId="voice-chat"
            title={t('voice.chat')}
            placeholder={t('voice.chatPlaceholder')}
            onSend={(text) => void session.sendChat(text)}
            onClearHistory={() => {
              session.clearChatHistory();
              notify(t('toast.historyCleared'));
            }}
          />
        </aside>
      </div>
    </main>
  );
}

function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('') || '?'
  );
}
